/**
 * LandingPage — public-facing landing with 6-article preview, email capture,
 * brief teaser, social proof strip and newsletter teaser.
 * Extracted from App.js (P3-2 refactor).
 */
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Lock, BookOpen } from "lucide-react";
import { API } from "../config";
import TickerBar from "../components/TickerBar";
import BriefOfWeek from "../components/BriefOfWeek";
import EmailCapture from "../components/EmailCapture";
import SocialProofStrip from "../components/SocialProofStrip";
import TrustedSourcesStrip from "../components/TrustedSourcesStrip";
import AdCard from "../components/AdCard";
import CreditsSection from "../components/CreditsSection";
import StickySubscribeBanner from "../components/StickySubscribeBanner";
import PublicPageFooter from "../components/PublicPageFooter";
import NewsCard from "../components/NewsCard";

const PHOCUSWRIGHT_QUOTE = {
  text: "More than half of business travelers now use AI for planning, booking and rebooking.",
  source: "Phocuswright Travel Forward 2026",
  href: "https://www.phocuswright.com/Travel-Research/Research-Updates/2026/Travel-Forward-Data-Insights-and-Trends-for-2026",
};

const timeAgo = (d) => {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const LandingPage = ({ darkMode }) => {
  const [articles, setArticles] = useState([]);
  const [latestNewsletter, setLatestNewsletter] = useState(null);
  const [adsense, setAdsense] = useState({ enabled: false, publisher_id: "", slot_id: "" });
  const [brief, setBrief] = useState(null);
  const [ticker, setTicker] = useState(null);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const navigate = useNavigate();
  const dm = darkMode;

  // P2 iter90: rotate the hero citation between the Phocuswright stat and
  // real fresh headlines from the feed.
  const quotes = [
    PHOCUSWRIGHT_QUOTE,
    ...articles.slice(0, 2).map(a => ({
      text: a.title,
      source: `${a.source} · from today's feed`,
      href: a.originalUrl,
    })),
  ];
  useEffect(() => {
    if (quotes.length <= 1) return;
    const t = setInterval(() => setQuoteIdx(i => (i + 1) % quotes.length), 7000);
    return () => clearInterval(t);
  }, [quotes.length]);  
  const activeQuote = quotes[quoteIdx % quotes.length];

  useEffect(() => {
    document.title = "TourismIntel | Tourism intelligence, not tourism noise";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "All the stories that matter in AI and travel, explained. A weekly brief, newsletter and podcast, plus Pulse: Italy's tourism in official numbers and forecasts.");
  }, []);

  useEffect(() => {
    axios.get(`${API}/news`).then(r => setArticles(r.data.articles || [])).catch(console.error);
    axios.get(`${API}/newsletters/latest`).then(r => setLatestNewsletter(r.data.newsletter)).catch(console.error);
    axios.get(`${API}/brief`).then(r => setBrief(r.data.brief)).catch(console.error);
    axios.get(`${API}/ticker`).then(r => setTicker(r.data)).catch(console.error);
    axios.get(`${API}/adsense-config`).then(r => {
      setAdsense(r.data);
      if (r.data.enabled && r.data.publisher_id) {
        const s = document.createElement("script");
        s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${r.data.publisher_id}`;
        s.async = true; s.crossOrigin = "anonymous";
        document.head.appendChild(s);
      }
    }).catch(console.error);
  }, []);

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const tickerVisible = ticker?.is_enabled && ticker?.items?.length > 0;

  // Inject an ad card every 5 articles
  const articlesWithAds = [];
  articles.forEach((a, i) => {
    articlesWithAds.push({ type: "article", data: a, key: `a-${i}` });
    if (adsense.enabled && (i + 1) % 5 === 0) {
      articlesWithAds.push({ type: "ad", key: `ad-${i}` });
    }
  });

  return (
    <div className={`min-h-screen ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
      {/* Ticker — very top, sticky */}
      <TickerBar config={ticker} darkMode={dm} />

      {/* Header — sticks below ticker when ticker visible */}
      <header className={`glass-nav sticky z-50 ${tickerVisible ? "top-8" : "top-0"}`}>
        <div className="max-w-screen-xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src={dm ? "/TI_logo_white_crop.webp" : "/TI_logo_color_crop.webp"}
              alt="tourismintel.ai"
              width={148}
              height={32}
              style={{ height: 32, width: 148, objectFit: "contain", maxWidth: "none" }}
            />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/travel-brief" data-testid="brief-landing-nav-link" className={`hidden sm:flex items-center gap-1.5 text-xs font-medium category-tag transition-colors ${dm ? "text-slate-400 hover:text-cyan-400" : "text-[#6b7a7d] hover:text-[#006874]"}`}>
              Brief
            </Link>
            <Link to="/podcast" className={`hidden sm:flex items-center gap-1.5 text-xs font-medium category-tag transition-colors ${dm ? "text-slate-400 hover:text-cyan-400" : "text-[#6b7a7d] hover:text-[#006874]"}`} data-testid="podcast-landing-nav-link">
              Podcast
            </Link>
            <Link to="/newsletters" data-testid="newsletter-landing-nav-link" className={`hidden sm:flex items-center gap-1.5 text-xs font-medium category-tag transition-colors ${dm ? "text-slate-400 hover:text-cyan-400" : "text-[#6b7a7d] hover:text-[#006874]"}`}>
              Newsletter
            </Link>
            <Link to="/reports" data-testid="reports-landing-nav-link" className={`hidden sm:flex items-center gap-1.5 text-xs font-medium category-tag transition-colors ${dm ? "text-slate-400 hover:text-emerald-300" : "text-[#6b7a7d] hover:text-emerald-700"}`}>
              Reports
              <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>NEW</span>
            </Link>
            <button onClick={() => navigate("/auth")} data-testid="login-btn" className={`flex items-center gap-1.5 px-5 py-2 rounded-full category-tag transition-all ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
              Sign up / Log in <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 pt-10 pb-32">
        {/* Hero CTA with email capture + live feed preview (P1 iter90) */}
        <div className={`rounded-2xl p-8 md:p-12 mb-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="md:grid md:grid-cols-5 md:gap-10 items-start">
            <div className="md:col-span-3">
              <p className={`category-tag mb-2 ${dm ? "text-cyan-400" : "text-[#006874]"}`}>CURATED · TRAVEL INDUSTRY · EVERY 12 HOURS</p>
              <h1 className={`editorial-heading text-4xl md:text-5xl font-medium tracking-tight mb-4 leading-tight ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
                Tourism intelligence,<br />not tourism noise
              </h1>
              <p className={`text-base mb-6 max-w-xl leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                All the stories that matter in AI and travel, explained. Each week, a brief, a newsletter and a podcast. The data room never closes.
              </p>

              {/* Rotating citation — external stat + live feed headlines (P2 iter90) */}
              <blockquote key={quoteIdx} data-testid="hero-rotating-quote" className={`mb-8 pl-4 border-l-2 max-w-lg animate-fade-in ${dm ? "border-cyan-400/40" : "border-[#006874]/30"}`} style={{ minHeight: 72 }}>
                <p className={`text-sm leading-relaxed italic ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  &quot;{activeQuote.text}&quot;
                </p>
                <footer className={`mt-1.5 text-xs ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  —{" "}
                  {activeQuote.href ? (
                    <a
                      href={activeQuote.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`underline underline-offset-2 transition-colors ${dm ? "hover:text-slate-400" : "hover:text-[#006874]"}`}
                    >
                      {activeQuote.source}
                    </a>
                  ) : (
                    activeQuote.source
                  )}
                </footer>
              </blockquote>
              <div className="max-w-md" style={{ minHeight: 80 }}>
                <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>CREATE YOUR FREE ACCOUNT — FULL FEED + WEEKLY BRIEF</p>
                <EmailCapture darkMode={dm} />
                <p className={`category-tag mt-2.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="signup-copy-clarifier">
                  No password — we email you a secure sign-in link · Unsubscribe anytime
                </p>
                <p className={`category-tag mt-1.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  Already a member?{" "}
                  <button onClick={() => navigate("/auth")} className={`${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}>Log in →</button>
                </p>
              </div>
            </div>

            {/* Live feed mini-preview — fills the empty right side of the hero */}
            <div className="hidden md:block md:col-span-2">
              <div data-testid="hero-feed-preview" className={`rounded-xl p-5 ${dm ? "bg-[#11181a]" : "bg-[#f8f9fa]"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.18)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full pulse-dot ${dm ? "bg-cyan-400" : "bg-[#006874]"}`} />
                  <p className={`category-tag ${dm ? "text-cyan-400" : "text-[#006874]"}`}>Live from the feed</p>
                </div>
                {articles.length === 0 ? (
                  <div className="py-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} className={`h-10 rounded-lg my-3 animate-pulse ${dm ? "bg-white/5" : "bg-black/5"}`} />
                    ))}
                  </div>
                ) : (
                  articles.slice(0, 3).map((a, i) => (
                    <button
                      key={a.id || i}
                      data-testid={`hero-feed-preview-item-${i}`}
                      onClick={() => navigate("/auth")}
                      className={`block w-full text-left py-3 group ${i < 2 ? `border-b ${dm ? "border-white/5" : "border-black/5"}` : ""}`}
                    >
                      <p className={`text-sm font-medium leading-snug line-clamp-2 transition-colors ${dm ? "text-slate-200 group-hover:text-cyan-400" : "text-[#191c1d] group-hover:text-[#006874]"}`}>
                        {a.title}
                      </p>
                      <p className={`category-tag mt-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                        {a.source} · {timeAgo(a.publishedAt)}
                      </p>
                    </button>
                  ))
                )}
                <button
                  onClick={() => navigate("/auth")}
                  data-testid="hero-feed-preview-cta"
                  className={`mt-3 category-tag flex items-center gap-1 transition-colors ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}
                >
                  Open the full feed <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Social proof strip */}
        <SocialProofStrip darkMode={dm} />

        {/* Trusted institutions logo strip (P2 iter90) */}
        <TrustedSourcesStrip darkMode={dm} />

        {/* Brief of the Week */}
        <BriefOfWeek brief={brief} darkMode={dm} />

        {/* Preview articles */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Preview</p>
            <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Latest in Tourism Intelligence</h2>
          </div>
          <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Showing 6 of many</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {articlesWithAds.map(item =>
            item.type === "article"
              ? <NewsCard key={item.key} article={item.data} darkMode={dm} isSelected={false} onSelect={() => {}} isAdmin={false} />
              : <AdCard key={item.key} publisherId={adsense.publisher_id} slotId={adsense.slot_id} darkMode={dm} />
          )}
        </div>

        {/* Lock wall */}
        <div className={`rounded-2xl p-10 text-center ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <Lock className={`w-7 h-7 mx-auto mb-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} />
          <h3 className={`editorial-heading text-2xl font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Join free to read the full feed</h3>
          <p className={`text-sm mb-6 max-w-sm mx-auto leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Enter your email and we&apos;ll send you a secure sign-in link — instant access to the full feed plus the weekly AI tourism digest. No password, no spam.</p>
          <div className="max-w-sm mx-auto">
            <EmailCapture darkMode={dm} />
          </div>
          <p className={`mt-4 text-xs ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Already a member? <button onClick={() => navigate("/auth")} className={`underline ${dm ? "text-cyan-400" : "text-[#006874]"}`}>Log in →</button></p>
        </div>

        {/* Latest Newsletter teaser */}
        {latestNewsletter && (
          <div className="mt-10">
            <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Latest Edition</p>
            <h2 className={`editorial-heading text-2xl font-medium mb-4 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>The Newsletter</h2>
            <div data-testid="public-newsletter-teaser" className={`rounded-2xl p-8 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }}>
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                    <span className={`category-tag ${dm ? "text-cyan-400" : "text-[#006874]"}`}>NEWSLETTER</span>
                  </div>
                  <h3 className={`editorial-heading text-xl font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{latestNewsletter.title}</h3>
                  <p className={`category-tag mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{fmtDate(latestNewsletter.published_at || latestNewsletter.created_at)} · {latestNewsletter.word_count} words · {latestNewsletter.article_count} articles curated</p>
                  <p className={`text-sm leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{latestNewsletter.content?.slice(0, 300)}…</p>
                </div>
                <button onClick={() => navigate("/auth")} className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full category-tag transition-all ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                  Read full edition <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Credits */}
        <CreditsSection darkMode={dm} brief={brief} />
      </main>

      {/* Sticky subscribe banner */}
      <StickySubscribeBanner darkMode={dm} />

      {/* Footer */}
      <PublicPageFooter dark={dm} />
    </div>
  );
};

export default LandingPage;
